// Numbers for the facilities Overview: one game's restroom data reduced to what the comparison
// needs, limited to the time window (offsets from each game's start), plus findings across the games.
import React from 'react';
import moment from 'moment';
import {
  CLEANING_THRESHOLD, QUEUE_TARGET, buildRestroomStats, combinedVisitSeries, visitFlow, peakQuarter, clockToMinute,
} from './facilitiesMetrics';
import { listNames } from './facilitiesInsights';
import { formatDuration } from '../Concession/live/liveMetrics';

// "about" times are rounded to 5 minutes: they summarise several games, so minute precision would mislead
const round5 = (m) => Math.round(m / 5) * 5;
const durationText = (m) => (m >= 60 ? formatDuration(m) : `${m} min`);

export const relativeToStart = (minutes) => {
  const m = round5(Math.abs(minutes));
  if (m < 5) return 'right at game start';
  return minutes < 0 ? `${durationText(m)} before game start` : `${durationText(m)} after game start`;
};

// A range of offsets: "15–45 min before game start", or across game start when it straddles it
const offsetRange = (lo, hi) => {
  const a = round5(lo);
  const b = round5(hi);
  if (a < 0 && b > 0) return `between ${relativeToStart(a)} and ${relativeToStart(b)}`;
  const near = Math.min(Math.abs(a), Math.abs(b));
  const far = Math.max(Math.abs(a), Math.abs(b));
  const side = b <= 0 ? 'before' : 'after';
  return far < 60 ? `between ${near} and ${far} min ${side} game start` : `between ${durationText(near)} and ${durationText(far)} ${side} game start`;
};

// Middle of a quarter-hour, relative to game start (minutes; negative = before)
export const midOffset = (slot, startMinute) => (slot.start + slot.end) / 2 - startMinute;

const pct = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0);
const avg = (values) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : null);

// One game: restrooms = the transformed list from fetchFacilitiesDay; window = { startOffset, endOffset }
export const buildRestroomGameMetrics = ({ restrooms, date, gameDate, window }) => {
  const dateKey = moment(date).format('YYYY-MM-DD');
  const startMinute = clockToMinute(gameDate?.gameStartTime);
  const range = startMinute != null
    ? { start: startMinute + window.startOffset, end: startMinute + window.endOffset }
    : null;

  const stats = restrooms.map((r) => buildRestroomStats(r, dateKey, null, range));
  const combined = combinedVisitSeries(stats.map((s) => s.series));
  const inRange = (m) => !range || (m >= range.start && m < range.end);
  const flow = visitFlow(combined, 1).filter((b) => inRange(b.start));

  const visits = stats.reduce((sum, s) => sum + s.visits, 0);
  const perRestroom = stats.map((s) => ({
    id: s.id,
    label: s.label,
    type: s.restroom.type,
    sectionId: s.restroom.sectionId,
    imageURL: s.restroom.imageURL,
    visits: s.visits,
    dueCount: s.dueCount,
    queuePeak: s.queuePeak,
    longMinutes: s.longMinutes,
  }));
  const peakRestroom = perRestroom.reduce((top, r) => (r.queuePeak && (!top || r.queuePeak.value > top.queuePeak.value) ? r : top), null);

  // Average queue across restrooms, minute by minute (restrooms with a reading that minute)
  const queueByMinute = new Map();
  stats.forEach((st) => st.queue.forEach((q) => {
    if (!inRange(q.minute)) return;
    const b = queueByMinute.get(q.minute) || { sum: 0, n: 0 };
    b.sum += q.queueTime;
    b.n += 1;
    queueByMinute.set(q.minute, b);
  }));
  const queuePoints = [...queueByMinute.entries()].sort((a, b) => a[0] - b[0]).map(([m, b]) => ({ m, v: b.sum / b.n }));
  const busiest = perRestroom.reduce((top, r) => (!top || r.visits > top.visits ? r : top), null);
  const byType = {};
  perRestroom.forEach((r) => { byType[r.type] = (byType[r.type] || 0) + r.visits; });
  const before = startMinute != null ? flow.filter((b) => b.start < startMinute).reduce((sum, b) => sum + b.visits, 0) : null;
  const dataStart = combined.length ? combined[0].minute : null;
  const dataEnd = combined.length ? combined[combined.length - 1].minute : null;

  return {
    hasData: combined.length > 0,
    startMinute,
    windowStart: range ? range.start : dataStart,
    windowEnd: range ? range.end : dataEnd,
    visits,
    dueCount: stats.reduce((sum, s) => sum + s.dueCount, 0),
    rush: peakQuarter(flow),
    busiest: busiest && busiest.visits > 0 ? busiest : null,
    peakRestroom,
    hasQueue: queuePoints.length > 0,
    avgQueue: queuePoints.length ? avg(queuePoints.map((p) => p.v)) : null,
    queuePoints,
    byType,
    beforeStartShare: before != null && visits ? pct(before, visits) : null,
    perRestroom,
    visitsPoints: flow.map((b) => ({ m: b.start, v: b.visits })),
  };
};

// "What stands out" across the selected games. cards: [{ date, label, color, m }]
export const buildFacilitiesOverviewInsights = (cards) => {
  const withData = cards.filter((c) => c.m?.hasData);
  if (!withData.length) return [];
  const multi = withData.length >= 2;
  const n = withData.length;
  const insights = [];

  // When the restrooms are busiest, relative to game start (middle of each game's busiest quarter-hour)
  const rushes = withData.filter((c) => c.m.rush && c.m.startMinute != null).map((c) => midOffset(c.m.rush, c.m.startMinute));
  if (rushes.length) {
    const lo = Math.min(...rushes);
    const hi = Math.max(...rushes);
    insights.push({
      key: 'rush',
      tone: 'info',
      icon: 'graph-up-arrow',
      score: 100,
      label: 'Busiest time',
      text: !multi ? (
        <>Restrooms were busiest <strong>{relativeToStart(rushes[0])}</strong>.</>
      ) : hi - lo <= 15 ? (
        <>Restrooms were busiest at the same point in every game: about <strong>{relativeToStart(avg(rushes))}</strong>.</>
      ) : (
        <>Restrooms were busiest <strong>{offsetRange(lo, hi)}</strong>, depending on the game.</>
      ),
    });
  }

  // Longest queues: the restroom with the most time at or over the target across the games
  const queueIds = Array.from(new Set(withData.flatMap((c) => c.m.perRestroom.filter((r) => r.queuePeak).map((r) => r.id))));
  if (queueIds.length) {
    const queueTotals = queueIds.map((id) => {
      const entries = withData.map((c) => c.m.perRestroom.find((r) => r.id === id)).filter(Boolean);
      const peak = entries.reduce((top, r) => (r.queuePeak && (!top || r.queuePeak.value > top.value) ? r.queuePeak : top), null);
      return { id, label: entries[0]?.label, longMinutes: entries.reduce((sum, r) => sum + r.longMinutes, 0), games: entries.length || 1, peak };
    }).sort((a, b) => b.longMinutes - a.longMinutes || b.peak.value - a.peak.value);
    const worst = queueTotals[0];
    const isLong = worst.longMinutes > 0;
    insights.push({
      key: 'queues',
      tone: isLong ? 'bad' : 'good',
      icon: 'hourglass-split',
      score: isLong ? 95 : 40,
      label: isLong ? 'Longest queues' : 'Queues',
      text: isLong ? (
        <>
          <strong>{worst.label}</strong> had the longest queues: at {QUEUE_TARGET} min or more for
          {' '}<strong>{formatDuration(multi ? worst.longMinutes / worst.games : worst.longMinutes)}</strong>
          {multi ? ' per game on average' : ''}, peaking at <strong>{worst.peak.value} min</strong>.
        </>
      ) : (
        <>Queues stayed short{multi ? ' in every game' : ''}: every restroom stayed under {QUEUE_TARGET} min.</>
      ),
    });
  }

  // Cleanings due per game, and the game that needed the most
  const dueTotal = withData.reduce((sum, c) => sum + c.m.dueCount, 0);
  const dueSorted = [...withData].sort((a, b) => b.m.dueCount - a.m.dueCount);
  const dueTop = dueSorted[0];
  insights.push({
    key: 'due',
    tone: dueTotal ? 'warn' : 'good',
    icon: 'clipboard-check',
    score: 90,
    label: 'Cleanings due',
    text: !dueTotal ? (
      <>No restroom reached {CLEANING_THRESHOLD} entries{multi ? ' in any game' : ''}, so no cleanings were due.</>
    ) : !multi ? (
      <><strong>{dueTotal}</strong> cleaning{dueTotal === 1 ? '' : 's'} due (one every {CLEANING_THRESHOLD} entries per restroom).</>
    ) : (
      <>
        About <strong>{Math.round((dueTotal / n) * 10) / 10}</strong> cleanings due per game (one every {CLEANING_THRESHOLD} entries per restroom)
        {dueTop.m.dueCount > dueSorted[dueSorted.length - 1].m.dueCount
          ? <>; the most on <strong>{dueTop.label.long}</strong> ({dueTop.m.dueCount})</>
          : ''}.
      </>
    ),
  });

  // Busiest restroom: most entries across the selected games combined, and how many games it led
  const ids = Array.from(new Set(withData.flatMap((c) => c.m.perRestroom.map((r) => r.id))));
  const labelOf = (id) => withData.flatMap((c) => c.m.perRestroom).find((r) => r.id === id)?.label;
  const allVisits = withData.reduce((sum, c) => sum + c.m.visits, 0);
  const totals = ids.map((id) => ({
    id,
    label: labelOf(id),
    visits: withData.reduce((sum, c) => sum + (c.m.perRestroom.find((r) => r.id === id)?.visits || 0), 0),
    led: withData.filter((c) => c.m.busiest?.id === id).length,
  })).sort((a, b) => b.visits - a.visits);
  const top = totals[0];
  if (top && top.visits > 0 && ids.length > 1) {
    const share = pct(top.visits, allVisits);
    const others = Array.from(new Set(
      withData.filter((c) => c.m.busiest && c.m.busiest.id !== top.id).map((c) => c.m.busiest.label)
    ));
    let text;
    if (!multi) {
      text = <><strong>{top.label}</strong> was the busiest restroom, with <strong>{share}%</strong> of entries.</>;
    } else if (top.led === n) {
      text = <><strong>{top.label}</strong> was the busiest restroom in <strong>every game</strong>, with <strong>{share}%</strong> of all entries.</>;
    } else if (top.led > 0) {
      text = (
        <>
          <strong>{top.label}</strong> had the most entries overall (<strong>{share}%</strong>) and was the busiest in
          {' '}<strong>{top.led} of {n}</strong> games{others.length ? <>; <strong>{listNames(others)}</strong> led the others</> : ''}.
        </>
      );
    } else {
      text = (
        <>
          <strong>{top.label}</strong> had the most entries overall (<strong>{share}%</strong>) but didn&apos;t lead any single game:
          {' '}<strong>{listNames(others)}</strong> {others.length > 1 ? 'each edged it out' : 'edged it out each time'}.
        </>
      );
    }
    insights.push({ key: 'busiest', tone: 'info', icon: 'person-walking', score: 80, label: 'Busiest restroom', text });
  }

  // Busiest game
  if (multi) {
    const sorted = [...withData].sort((a, b) => b.m.visits - a.m.visits);
    const most = sorted[0];
    const least = sorted[sorted.length - 1];
    if (least.m.visits > 0 && most.m.visits > least.m.visits) {
      insights.push({
        key: 'game',
        tone: 'info',
        icon: 'calendar-event',
        score: 70,
        label: 'Busiest game',
        text: (
          <>
            <strong>{most.label.long}</strong> had the most entries (<strong>{most.m.visits.toLocaleString()}</strong>),
            {' '}<strong>{pct(most.m.visits - least.m.visits, least.m.visits)}%</strong> more than {least.label.long} ({least.m.visits.toLocaleString()}).
          </>
        ),
      });
    }
  }

  // Before game start
  const shares = withData.map((c) => c.m.beforeStartShare).filter((v) => v != null);
  if (shares.length) {
    const a = Math.round(avg(shares));
    insights.push({
      key: 'before',
      tone: 'info',
      icon: 'clock-history',
      score: 60,
      label: 'Before game start',
      text: (
        <>
          {multi ? 'On average, ' : ''}<strong>{a}%</strong> of entries came before game start
          {multi && Math.max(...shares) - Math.min(...shares) >= 10 ? ` (${Math.min(...shares)}–${Math.max(...shares)}% by game)` : ''}.
        </>
      ),
    });
  }

  // Men's vs women's
  const mens = withData.reduce((sum, c) => sum + (c.m.byType.mens || 0), 0);
  const womens = withData.reduce((sum, c) => sum + (c.m.byType.womens || 0), 0);
  const hasBoth = withData.some((c) => c.m.byType.mens != null && c.m.byType.womens != null);
  if (hasBoth && mens + womens > 0) {
    const womensShare = pct(womens, mens + womens);
    const lead = womensShare >= 50 ? { name: "Women's", share: womensShare } : { name: "Men's", share: 100 - womensShare };
    insights.push({
      key: 'type',
      tone: 'info',
      icon: 'people-fill',
      score: 55,
      label: "Men's and women's",
      text: Math.abs(womensShare - 50) <= 3
        ? <>Men&apos;s and women&apos;s restrooms were used about evenly.</>
        : <><strong>{lead.name}</strong> restrooms took <strong>{lead.share}%</strong> of men&apos;s and women&apos;s entries{multi ? ' across the games' : ''}.</>,
    });
  }

  return insights.sort((a, b) => b.score - a.score).slice(0, 6);
};
